'use client'

import Image from 'next/image'
import { useMemo, useState } from 'react'
import { cn } from '@desoutter/uikit'
import { PlayIcon } from 'lucide-react'

function extractYouTubeId(input?: string): string | null {
	if (!input) return null
	try {
		// Handle various YouTube URL formats
		// - https://www.youtube.com/watch?v=VIDEO_ID
		// - https://youtu.be/VIDEO_ID
		// - https://www.youtube.com/embed/VIDEO_ID
		const url = new URL(input)
		const host = url.hostname.toLowerCase()
		const isYouTubeHost =
			host.includes('youtube.com') || host.includes('youtu.be')
		if (!isYouTubeHost) return null
		if (host.includes('youtu.be')) {
			return url.pathname.replace('/', '') || null
		}
		if (url.searchParams.get('v')) {
			return url.searchParams.get('v')
		}
		const pathParts = url.pathname.split('/')
		const embedIndex = pathParts.findIndex((p) => p === 'embed')
		if (embedIndex !== -1 && pathParts[embedIndex + 1]) {
			return pathParts[embedIndex + 1]
		}
		// Fallback: try last path segment
		return pathParts[pathParts.length - 1] || null
	} catch (_) {
		// If it's not a URL, maybe it's a raw ID
		// Only accept raw IDs if we are explicitly passed an ID (developer responsibility)
		const idLike = input.trim()
		const idRegex = /^[a-zA-Z0-9_-]{8,}$/
		return idRegex.test(idLike) ? idLike : null
	}
}

function buildEmbedUrl(id: string, autoplay: boolean): string {
	const base = `https://www.youtube.com/embed/${id}`
	const params = new URLSearchParams({
		autoplay: autoplay ? '1' : '0',
		playsinline: '1',
		modestbranding: '1',
		rel: '0',
	})
	return `${base}?${params.toString()}`
}

type YouTubePlayerProps = {
	url: string
	title?: string
	className?: string
	thumbnailQuality?: 'maxresdefault' | 'hqdefault' | 'mqdefault'
}

export default function YouTubePlayer({
	url,
	title,
	className,
	thumbnailQuality = 'maxresdefault',
}: YouTubePlayerProps) {
	const [isPlaying, setIsPlaying] = useState(false)
	const videoId = useMemo(() => extractYouTubeId(url), [url])
	const thumbnailUrl = useMemo(() => {
		return videoId
			? `https://img.youtube.com/vi/${videoId}/${thumbnailQuality}.jpg`
			: undefined
	}, [videoId, thumbnailQuality])

	if (!videoId) return null

	return (
		<div
			className={cn(
				'relative w-full aspect-video rounded-2xl overflow-hidden',
				className,
			)}
		>
			{!isPlaying && thumbnailUrl ? (
				<button
					type="button"
					className={cn(
						'group absolute inset-0 w-full h-full',
						'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500',
					)}
					onClick={() => setIsPlaying(true)}
					aria-label={title ? `Play video: ${title}` : 'Play video'}
				>
					<Image
						src={thumbnailUrl}
						alt={title || 'YouTube video thumbnail'}
						fill
						className="object-cover"
						priority={false}
					/>
					{/* Play button */}
					<div className="absolute inset-0 flex items-center justify-center transition-transform group-hover:scale-105">
						<div className="bg-white/90 rounded-full p-4 shadow-xl">
							<PlayIcon className="w-8 h-8 text-primary" />
						</div>
					</div>
				</button>
			) : (
				<iframe
					className="w-full h-full"
					src={buildEmbedUrl(videoId, true)}
					title={title || 'YouTube video player'}
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					allowFullScreen
				/>
			)}
		</div>
	)
}
